import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  message,
  Spin,
  Table,
  Flex,
} from "antd";
import { MailOutlined, BankOutlined, TableOutlined } from "@ant-design/icons";

import http from "@/utils/http";
import api from "@/utils/api";
import commonFn from "@/utils/commonFn";
/* ---------------------- 懒加载下拉 Hook(移到组件外) ---------------------- */
function useLazySelect(apiUrl, mapper, enableCache = true) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(null);

  const load = async (forceReload = false) => {
    if (options.length && enableCache && !forceReload) return options;
    if (loadingRef.current && !forceReload) return loadingRef.current;

    const request = (async () => {
      setLoading(true);
      try {
        const res = await http.get(apiUrl);
        const rawData = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.owner)
            ? res.data.owner
            : res?.data || [];
        const nextOptions = mapper(rawData);
        setOptions(nextOptions);
        return nextOptions;
      } catch (err) {
        console.error("Select load error:", err);
        setOptions([]);
        return [];
      } finally {
        setLoading(false);
        loadingRef.current = null;
      }
    })();

    loadingRef.current = request;
    return request;
  };

  return { options, loading, load };
}

const Edit = forwardRef((props, ref) => {
  const { onSuccess } = props;
  const [initLoading, setInitLoading] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [trustId, setTrustId] = useState(null);
  // 保存接口返回的完整数据,用于展示银行表格
  const [trustData, setTrustData] = useState(null);

  /* ---------------------- Select Data ---------------------- */
  const stateSelect = useLazySelect(api.getStateDictList, (data) =>
    Object.entries(data || {}).map(([key, label]) => ({
      label,
      value: key,
    })),
  );

  const ownerSelect = useLazySelect(api.getOwnerDictList, (data) =>
    data.map((d) => ({
      label: d.titleName,
      value: d.ownerId,
    })),
  );

  const glChartNameOptions = [
    { label: "Z64", value: 1 },
    { label: "GEN", value: 2 },
    { label: "STORE", value: 3 },
  ];

  const serviceNameOptions = [
    { label: "Z64", value: 1 },
    { label: "GEN", value: 2 },
  ];

  /* ---------------------- 详情加载 ---------------------- */
  const loadDetail = async (id) => {
    try {
      setInitLoading(true);

      const res = await http.get(api.getTrustByIdData, {
        params: { trustId: id },
      });
      const detail = res.data || {};
      const ownerOptions = await ownerSelect.load();
      const matchedOwner = ownerOptions.find(
        (item) => String(item?.value) === String(detail?.ownerId),
      );

      setTrustId(detail.trustId);
      setTrustData(detail); // ★ 保存完整数据

      form.setFieldsValue({
        ...detail,
        glChartNameId: detail.glChartNameId
          ? Number(detail.glChartNameId)
          : undefined,

        serviceNameId: detail.serviceNameId
          ? Number(detail.serviceNameId)
          : undefined,

        ownerId: matchedOwner?.value ?? detail.ownerId ?? undefined,
        active:
          detail.active !== undefined && detail.active !== null
            ? String(detail.active)
            : undefined,
      });
    } catch (err) {
      console.error(err);
      message.error("Failed to load trust detail");
    } finally {
      setInitLoading(false);
    }
  };

  const handleClose = () => {
    setVisible(false);
    form.resetFields();
    setTrustData(null);
  };

  useImperativeHandle(ref, () => ({
    open: async (record) => {
      form.resetFields();
      setTrustData(null);
      setVisible(true);
      if (record?.trustId) {
        await loadDetail(record.trustId);
      }
    },

    setModelAdd: async () => {
      form.resetFields();
      setTrustData(null);
      setVisible(true);
      await ownerSelect.load();
    },
  }));

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const payload = {
        trustId: trustId,
        ...values,
      };

      const res = await http.post(api.getTrustEdit, payload);

      if (res?.code === "1") {
        message.success("Trust updated successfully");
        handleClose();
        onSuccess && onSuccess();
      } else {
        message.error(res?.msg || "Update failed");
      }
    } catch (err) {
      console.error(err);
      message.error("Request failed");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------- Bank Accounts 表格列 ---------------------- */
  const bankColumns = [
    {
      title: "Bank Account",
      dataIndex: "bankAccount",
      key: "bankAccount",
      align: "center",
    },
    {
      title: "Bank Name",
      dataIndex: "bankName",
      key: "bankName",
      align: "center",
      ellipsis: true,
    },
    {
      title: "Cash GL",
      dataIndex: "cashGl",
      key: "cashGl",
      align: "center",
    },
    {
      title: "PPM Bank#",
      dataIndex: "ppmBankNumber",
      key: "ppmBankNumber",
      align: "center",
      width: 100,
    },
    {
      title: "Account Remark",
      dataIndex: "accountRemark",
      key: "accountRemark",
      align: "center",
    },
  ];

  return (
    <Modal
      title={
        <div style={{ fontSize: "24px", fontWeight: "bold", color: "red" }}>
          <MailOutlined style={{ marginRight: "8px" }} />
          EDIT ENTITY
          <div
            style={{ borderTop: "3px solid #3c8dbc", marginTop: "8px" }}
          ></div>
        </div>
      }
      width="60%"
      centered
      open={visible}
      maskClosable={false}
      onCancel={handleClose}
      footer={
        <div style={{ display: "flex", justifyContent: "center", gap: "16px" }}>
          <Button key="close" onClick={handleClose}>
            Close
          </Button>
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={() => form.submit()}
          >
            Submit
          </Button>
        </div>
      }
    >
      <Spin spinning={initLoading}>
        <Flex gap={16} style={{ width: "100%" }} align="flex-start">
          {/* 左列:表单 */}
          <div
            style={{
              flex: "0 0 35%",
              minWidth: 480,
              maxHeight: "calc(100vh - 300px)",
              overflowY: "auto",
            }}
          >
            <Form
              form={form}
              layout="horizontal"
              onFinish={onFinish}
              labelCol={{ style: { width: "180px" } }}
              className="compact-form"
            >
              <div style={{ width: "100%" }}>
                {/* <div className="modal-section-title">
                  <BankOutlined /> Trust Detail
                </div> */}

                <Form.Item
                  name="trust"
                  label="Entity Code"
                  rules={[{ required: true }]}
                >
                  <Input disabled />
                </Form.Item>

                <Form.Item
                  name="trustName"
                  label="Entity Name"
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>

                <Form.Item
                  name="ownerId"
                  label="Owner"
                  rules={[{ required: true }]}
                >
                  <Select
                    placeholder="Select Owner"
                    showSearch
                    allowClear
                    options={ownerSelect.options}
                    loading={ownerSelect.loading}
                    optionFilterProp="label"
                    filterOption={(input, option) => {
                      const keyword = input.toLowerCase();
                      const label =
                        option?.label?.toString().toLowerCase() || "";
                      const value =
                        option?.value?.toString().toLowerCase() || "";

                      return label.includes(keyword) || value.includes(keyword);
                    }}
                  />
                </Form.Item>

                <Form.Item
                  name="glChartNameId"
                  label="GL Chart Name"
                  rules={[{ required: true }]}
                >
                  <Select options={glChartNameOptions} />
                </Form.Item>

                <Form.Item
                  name="serviceNameId"
                  label="Service Name"
                  rules={[{ required: true }]}
                >
                  <Select options={serviceNameOptions} />
                </Form.Item>

                <Form.Item name="street" label="Street">
                  <Input />
                </Form.Item>

                <Form.Item name="city" label="City">
                  <Input />
                </Form.Item>

                <Form.Item name="county" label="County">
                  <Input />
                </Form.Item>

                <Form.Item name="state" label="State">
                  <Select
                    placeholder="Select State"
                    options={stateSelect.options}
                    loading={stateSelect.loading}
                    showSearch
                    optionFilterProp="label"
                    onDropdownVisibleChange={(open) =>
                      open && stateSelect.load()
                    }
                  />
                </Form.Item>

               <Form.Item
  name="zip"
  label="Zip"
  rules={[{ validator: commonFn.zipValidator }]}
>
  <Input placeholder="Zip" />
</Form.Item>

                <Form.Item name="fedId" label="Federal Tax ID">
                  <Input />
                </Form.Item>

                <Form.Item name="stateId" label="State Tax ID">
                  <Input />
                </Form.Item>

                <Form.Item name="phone" label="Phone">
                  <Input />
                </Form.Item>

                <Form.Item
                  name="active"
                  label="Active"
                  rules={[{ required: true }]}
                >
                  <Select
                    options={[
                      { value: "1", label: "Yes" },
                      { value: "0", label: "No" },
                    ]}
                  />
                </Form.Item>
              </div>
            </Form>
          </div>

          {/* 右列:Bank Accounts */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {trustData && (
              <>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: "16px",
                    marginBottom: 12,
                    color: "#1E293B",
                  }}
                >
                  <TableOutlined style={{ marginRight: 8 }} />
                  Total Bank Accounts:{" "}
                  {trustData.bankCount ?? trustData.bankAccounts?.length ?? 0}
                </div>
                <Table
                  columns={bankColumns}
                  dataSource={trustData.bankAccounts || []}
                  pagination={false}
                  size="small"
                  bordered
                  scroll={{ x: "max-content", y: "calc(100vh - 380px)" }}
                  rowKey="bankId"
                />
              </>
            )}
          </div>
        </Flex>
      </Spin>
    </Modal>
  );
});

export default Edit;